import { defineMessages } from "../i18n/core";

// Relatórios de evidência (ADR 0026). Os títulos e descrições espelham os do
// documento gerado no servidor.
export const reportsMessages = defineMessages({
  "pt-BR": {
    title: "Relatórios",
    lede: "Evidência para auditoria em PDF/A (ISO 19005) ou CSV, por período. Cada emissão fica registrada com o hash do arquivo.",
    period: "Período",
    presetThisMonth: "Este mês",
    presetLastMonth: "Mês passado",
    presetLast30: "Últimos 30 dias",
    presetQuarter: "Últimos 90 dias",
    from: "De",
    to: "Até",
    format: "Formato",
    formatPdf: "PDF/A",
    formatCsv: "CSV",
    csvNote: "O CSV traz a tabela principal do relatório; o PDF/A é o documento completo.",
    issue: "Emitir",
    issued: "Emissões",
    issuedHint: "Guarde o arquivo: o hash gravado aqui e na auditoria prova que ele é o documento emitido.",
    noIssued: "Nenhum relatório emitido ainda.",
    colReport: "Relatório",
    colPeriod: "Período",
    colFormat: "Formato",
    colIssuedBy: "Emitido por",
    colIssuedAt: "Emitido em",
    colHash: "SHA-256",
    copyHash: "Copiar o hash",
    hashCopied: "Hash copiado.",
    started: (name: string) => `Emitindo ${name}. O download começa em instantes.`,
    invalidPeriod: "O fim precisa ser depois do início, e o período no máximo um ano.",
    kinds: {
      summary: { title: "Resumo de proteção", desc: "Máquinas, execuções, taxa de sucesso, volume protegido, alertas e RPO no período.", controls: "ISO 27001 A.8.13" },
      executions: { title: "Execução de backups", desc: "Cada trabalho do período com início, fim, resultado e volume; falhas e quando foram resolvidas.", controls: "ISO 27001 A.8.13, A.8.15" },
      coverage: { title: "Cobertura e políticas", desc: "Cada máquina com política, caminhos, agenda, retenção e RPO; imagens e bancos; máquinas sem backup.", controls: "ISO 27001 A.8.13" },
      rpo: { title: "RPO e RTO", desc: "RPO da política versus maior intervalo observado por máquina; restaurações e sua duração.", controls: "ISO 22301; ISO 27001 A.5.30" },
      restores: { title: "Testes de restauração", desc: "Cada restauração: quem pediu, de que ponto, o quê, destino, resultado e duração.", controls: "ISO 27001 A.8.13" },
      retention: { title: "Retenção e imutabilidade", desc: "Pontos guardados e apagados pela retenção, armazenamento, réplica e criptografia.", controls: "ISO 27001 A.8.13, A.8.10; LGPD" },
      audit: { title: "Trilha de auditoria", desc: "Quem fez o quê no período, com alvo e endereço de origem.", controls: "ISO 27001 A.8.15" },
      access: { title: "Acessos", desc: "Usuários, papéis, segundo fator, último acesso e tokens de API.", controls: "ISO 27001 A.5.15, A.5.18" },
      alerts: { title: "Alertas", desc: "Alertas abertos e resolvidos no período e tempo até resolver.", controls: "ISO 27001 A.8.16" },
    } as Record<string, { title: string; desc: string; controls: string }>,
  },
  "en-US": {
    title: "Reports",
    lede: "Audit evidence as PDF/A (ISO 19005) or CSV, by period. Every issue is recorded with the file hash.",
    period: "Period",
    presetThisMonth: "This month",
    presetLastMonth: "Last month",
    presetLast30: "Last 30 days",
    presetQuarter: "Last 90 days",
    from: "From",
    to: "To",
    format: "Format",
    formatPdf: "PDF/A",
    formatCsv: "CSV",
    csvNote: "CSV carries the report's main table; PDF/A is the complete document.",
    issue: "Issue",
    issued: "Issued",
    issuedHint: "Keep the file: the hash recorded here and in the audit trail proves it is the document that was issued.",
    noIssued: "No report issued yet.",
    colReport: "Report",
    colPeriod: "Period",
    colFormat: "Format",
    colIssuedBy: "Issued by",
    colIssuedAt: "Issued at",
    colHash: "SHA-256",
    copyHash: "Copy hash",
    hashCopied: "Hash copied.",
    started: (name: string) => `Issuing ${name}. The download starts shortly.`,
    invalidPeriod: "The end must be after the start, and the period at most one year.",
    kinds: {
      summary: { title: "Protection summary", desc: "Machines, runs, success rate, protected volume, alerts and RPO in the period.", controls: "ISO 27001 A.8.13" },
      executions: { title: "Backup execution", desc: "Every job in the period with start, end, result and volume; failures and when they were resolved.", controls: "ISO 27001 A.8.13, A.8.15" },
      coverage: { title: "Coverage and policies", desc: "Each machine with policy, paths, schedule, retention and RPO; images and databases; machines without backup.", controls: "ISO 27001 A.8.13" },
      rpo: { title: "RPO and RTO", desc: "Policy RPO versus the largest gap observed per machine; restores and their duration.", controls: "ISO 22301; ISO 27001 A.5.30" },
      restores: { title: "Restore tests", desc: "Every restore: who asked, from which point, what, destination, result and duration.", controls: "ISO 27001 A.8.13" },
      retention: { title: "Retention and immutability", desc: "Points kept and pruned by retention, storage, replica and encryption.", controls: "ISO 27001 A.8.13, A.8.10; LGPD" },
      audit: { title: "Audit trail", desc: "Who did what in the period, with target and source address.", controls: "ISO 27001 A.8.15" },
      access: { title: "Access", desc: "Users, roles, second factor, last login and API tokens.", controls: "ISO 27001 A.5.15, A.5.18" },
      alerts: { title: "Alerts", desc: "Alerts opened and resolved in the period and time to resolve.", controls: "ISO 27001 A.8.16" },
    } as Record<string, { title: string; desc: string; controls: string }>,
  },
  es: {
    title: "Informes",
    lede: "Evidencia para auditoría en PDF/A (ISO 19005) o CSV, por período. Cada emisión queda registrada con el hash del archivo.",
    period: "Período",
    presetThisMonth: "Este mes",
    presetLastMonth: "Mes pasado",
    presetLast30: "Últimos 30 días",
    presetQuarter: "Últimos 90 días",
    from: "Desde",
    to: "Hasta",
    format: "Formato",
    formatPdf: "PDF/A",
    formatCsv: "CSV",
    csvNote: "El CSV trae la tabla principal del informe; el PDF/A es el documento completo.",
    issue: "Emitir",
    issued: "Emisiones",
    issuedHint: "Guarde el archivo: el hash registrado aquí y en la auditoría prueba que es el documento emitido.",
    noIssued: "Ningún informe emitido todavía.",
    colReport: "Informe",
    colPeriod: "Período",
    colFormat: "Formato",
    colIssuedBy: "Emitido por",
    colIssuedAt: "Emitido el",
    colHash: "SHA-256",
    copyHash: "Copiar el hash",
    hashCopied: "Hash copiado.",
    started: (name: string) => `Emitiendo ${name}. La descarga empieza en instantes.`,
    invalidPeriod: "El fin debe ser posterior al inicio, y el período como máximo un año.",
    kinds: {
      summary: { title: "Resumen de protección", desc: "Máquinas, ejecuciones, tasa de éxito, volumen protegido, alertas y RPO en el período.", controls: "ISO 27001 A.8.13" },
      executions: { title: "Ejecución de copias", desc: "Cada trabajo del período con inicio, fin, resultado y volumen; fallos y cuándo se resolvieron.", controls: "ISO 27001 A.8.13, A.8.15" },
      coverage: { title: "Cobertura y políticas", desc: "Cada máquina con política, rutas, horario, retención y RPO; imágenes y bases; máquinas sin copia.", controls: "ISO 27001 A.8.13" },
      rpo: { title: "RPO y RTO", desc: "RPO de la política frente al mayor intervalo observado por máquina; restauraciones y su duración.", controls: "ISO 22301; ISO 27001 A.5.30" },
      restores: { title: "Pruebas de restauración", desc: "Cada restauración: quién la pidió, desde qué punto, qué, destino, resultado y duración.", controls: "ISO 27001 A.8.13" },
      retention: { title: "Retención e inmutabilidad", desc: "Puntos conservados y borrados por retención, almacenamiento, réplica y cifrado.", controls: "ISO 27001 A.8.13, A.8.10; LGPD" },
      audit: { title: "Registro de auditoría", desc: "Quién hizo qué en el período, con objetivo y dirección de origen.", controls: "ISO 27001 A.8.15" },
      access: { title: "Accesos", desc: "Usuarios, roles, segundo factor, último acceso y tokens de API.", controls: "ISO 27001 A.5.15, A.5.18" },
      alerts: { title: "Alertas", desc: "Alertas abiertas y resueltas en el período y tiempo hasta resolver.", controls: "ISO 27001 A.8.16" },
    } as Record<string, { title: string; desc: string; controls: string }>,
  },
});
